import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Overview', href: '/overview' },
    {
      name: 'Our Network',
      href: '/compare',
      dropdown: [
        { name: 'Med-Care Providers', href: '/compare', desc: 'Primary & Mental Health' },
        { name: 'Celebrity Home Health', href: '/compare', desc: 'Skilled Nursing & Therapy' },
        { name: 'Accent Senior Care', href: '/compare', desc: 'Personal & Companion Care' },
        { name: 'Peace of Mind Caregivers', href: '/compare', desc: 'Memory Care Specialists' },
      ],
    },
    {
      name: 'Services',
      href: '/referral',
      dropdown: [
        { name: 'Request Care', href: '/referral', desc: 'Start your care journey' },
        { name: 'Emergency Support', href: '/emergency', desc: '24/7 immediate response' },
        { name: 'Insurance Verification', href: '/insurance', desc: 'Check your coverage' },
      ],
    },
    { name: 'Professionals', href: '/professionals' },
    { name: 'Resources', href: '/resources' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleDropdownToggle = (itemName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === itemName ? null : itemName);
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white border-b border-stone-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
            <div className="w-9 h-9 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-heart-pulse-line text-white text-base"></i>
            </div>
            <div className="flex flex-col leading-tight">
              <span className={`font-bold text-sm leading-tight whitespace-nowrap transition-colors duration-300 ${isScrolled ? 'text-stone-900' : 'text-white'}`}>
                The Villarreal Health
              </span>
              <span className={`font-bold text-sm leading-tight whitespace-nowrap transition-colors duration-300 ${isScrolled ? 'text-teal-600' : 'text-teal-300'}`}>
                Network
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.dropdown ? (
                  <>
                    <button
                      onClick={(e) => handleDropdownToggle(item.name, e)}
                      className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        isScrolled
                          ? activeDropdown === item.name
                            ? 'text-teal-700 bg-teal-50'
                            : 'text-stone-700 hover:text-teal-700 hover:bg-stone-50'
                          : activeDropdown === item.name
                          ? 'text-white bg-white/20'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {item.name}
                      <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`}></i>
                    </button>

                    {activeDropdown === item.name && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl border border-stone-100 py-2 z-50 animate-[fadeIn_0.15s_ease]">
                        {item.dropdown.map((dropItem) => (
                          <Link
                            key={dropItem.name}
                            to={dropItem.href}
                            className="flex flex-col px-4 py-2.5 hover:bg-teal-50 transition-colors duration-150"
                            onClick={() => setActiveDropdown(null)}
                          >
                            <span className="text-sm font-semibold text-stone-800 hover:text-teal-700">{dropItem.name}</span>
                            <span className="text-xs text-stone-400 mt-0.5">{dropItem.desc}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      isScrolled
                        ? isActive(item.href)
                          ? 'text-teal-700 bg-teal-50'
                          : 'text-stone-700 hover:text-teal-700 hover:bg-stone-50'
                        : isActive(item.href)
                        ? 'text-white bg-white/20'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <a
              href="tel:+17025551234"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap ${
                isScrolled
                  ? 'text-teal-700 bg-teal-50 hover:bg-teal-100'
                  : 'text-white bg-white/15 hover:bg-white/25'
              }`}
            >
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse flex-shrink-0"></span>
              (702) 555-1234
            </a>
            <Link
              to="/referral"
              className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              Get Care
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors duration-200 cursor-pointer ${isScrolled ? 'text-stone-700 hover:bg-stone-100' : 'text-white hover:bg-white/10'}`}
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.dropdown ? (
                  <>
                    <button
                      onClick={(e) => handleDropdownToggle(item.name, e)}
                      className="flex items-center justify-between w-full px-3 py-2.5 text-stone-700 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors duration-150 cursor-pointer"
                    >
                      <span className="text-sm font-semibold">{item.name}</span>
                      <i className={`ri-arrow-down-s-line transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`}></i>
                    </button>
                    {activeDropdown === item.name && (
                      <div className="ml-4 mt-1 pl-3 border-l-2 border-teal-100 space-y-1">
                        {item.dropdown.map((dropItem) => (
                          <Link
                            key={dropItem.name}
                            to={dropItem.href}
                            className="block px-3 py-2 text-sm text-stone-600 hover:text-teal-700 hover:bg-teal-50 rounded-md transition-colors duration-150"
                            onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                          >
                            {dropItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`block px-3 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-150 ${
                      isActive(item.href) ? 'text-teal-700 bg-teal-50' : 'text-stone-700 hover:text-teal-700 hover:bg-teal-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-3 mt-3 border-t border-stone-100 space-y-2">
              <a href="tel:+17025551234" className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-teal-700 bg-teal-50 rounded-lg">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                (702) 555-1234
              </a>
              <Link
                to="/referral"
                className="block w-full text-center bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Get Care Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
